import { ExpandedBag } from "../utils/bag.type";
import { BagSdkMethod } from "../utils/method-initialiser";

type NextDayDeliveryInfo = {
  shouldShowBanner: boolean;
  expiryDate: string | null;
  isExpired: boolean;
} | null;

type PublicGetNextDayDeliveryMethod = (
  expandedBag: ExpandedBag,
) => NextDayDeliveryInfo;
type GetNextDayDeliveryMethod = BagSdkMethod<PublicGetNextDayDeliveryMethod>;

const NEXT_DAY_DELIVERY_CONFIG = {
  GB: { optionIds: [2], methodIds: [1] },
} as const;

const getValidOptionIds = (country: string): readonly number[] =>
  NEXT_DAY_DELIVERY_CONFIG[country as keyof typeof NEXT_DAY_DELIVERY_CONFIG]
    ?.optionIds || [];

const getValidMethodIds = (country: string): readonly number[] =>
  NEXT_DAY_DELIVERY_CONFIG[country as keyof typeof NEXT_DAY_DELIVERY_CONFIG]
    ?.methodIds || [];

const toMidnight = (date: string | Date): number => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate.getTime();
};

const getTomorrowMidnight = (): number => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return toMidnight(tomorrow);
};

export const getNextDayDeliveryInitialiser: GetNextDayDeliveryMethod =
  ({ country }) =>
  (expandedBag) => {
    if (!expandedBag?.delivery?.options) return null;

    const nextDayOption = expandedBag.delivery.options.find(
      (option) =>
        getValidOptionIds(country).includes(option.deliveryOptionId) &&
        getValidMethodIds(country).includes(option.deliveryMethodId),
    );

    if (!nextDayOption) return null;

    const hasAllLocalItems = expandedBag.items.every(
      (item) => item.itemType !== "Product" || item.source == null,
    );

    const expiryDate = nextDayOption.expiryDate ?? null;
    const isExpired = !!expiryDate && new Date(expiryDate) < new Date();

    const isDeliveryTomorrow =
      nextDayOption.estimatedDeliveryDate &&
      toMidnight(nextDayOption.estimatedDeliveryDate) === getTomorrowMidnight();

    const shouldShowBanner = Boolean(
      isDeliveryTomorrow && hasAllLocalItems && !isExpired && country === "GB",
    );

    return {
      shouldShowBanner,
      expiryDate,
      isExpired,
    };
  };
